import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { LoginShell } from '@/auth/LoginShell';
import { redirectQuery, safeRedirect } from '@/auth/LoginPage';
import type { AuthUser } from '@/auth/types';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Loading } from '@/components/Loading';
import { APP_NAME } from '@/components/PageHeader';
import { TextField } from '@/components/TextField';
import { ApiError, errorMessage } from '@/lib/api';
import { mfaApi, useMfaStatus } from './api';
import { MfaCodeInput, MfaEnrollment, RecoveryCodes } from './MfaEnrollment';
import './security.css';

/** 確認待ちセッションの状態を確認し、違う画面なら適切な画面へ戻す */
function usePendingGuard(expected: 'verify' | 'setup') {
  const { user } = useAuth();
  const status = useMfaStatus(!user);
  const [params] = useSearchParams();
  const redirect = safeRedirect(params.get('redirect'));
  let to: string | null = null;
  if (user) to = redirect;
  else if (status.error) to = '/login';
  else if (status.data && status.data.pending !== expected) {
    to = status.data.pending === 'verify' ? `/login/mfa${redirectQuery(redirect)}` : status.data.pending === 'setup' ? `/login/mfa-setup${redirectQuery(redirect)}` : '/login';
  }
  return { status, redirect, to };
}

function BackToLogin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="bs-login__back"
      onClick={async () => {
        await logout();
        navigate('/login', { replace: true });
      }}
    >
      <ArrowLeft size={14} aria-hidden />
      ログイン画面に戻る
    </button>
  );
}

/** 2段階認証：確認コード入力（ログイン時） */
export function MfaVerifyPage() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const { status, redirect, to } = usePendingGuard('verify');
  const [mode, setMode] = useState<'code' | 'recovery'>('code');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = `2段階認証｜${APP_NAME}`;
  }, []);

  if (to) return <Navigate to={to} replace />;
  if (status.isPending) return <Loading fullscreen label="ログイン状態を確認しています" />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === 'code' && code.length !== 6) {
      setError('認証アプリに表示されている 6 桁の確認コードを入力してください。');
      return;
    }
    if (mode === 'recovery' && recovery.trim().length < 8) {
      setError('リカバリーコード（例：abcde-23456）を入力してください。');
      return;
    }
    setSubmitting(true);
    try {
      const user = await mfaApi.verify(mode === 'code' ? { code } : { recoveryCode: recovery.trim() });
      const { recoveryCodesRemaining, ...me } = user;
      completeLogin(me as AuthUser);
      navigate(redirect, { replace: true, state: recoveryCodesRemaining != null ? { recoveryCodesRemaining } : undefined });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // 確認待ちの期限切れ（10 分）
        navigate('/login', { replace: true });
        return;
      }
      setError(errorMessage(err));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoginShell>
      <h1 className="bs-login__title bs-login__title--sm">2段階認証</h1>
      <p className="bs-login__sub">
        {mode === 'code'
          ? 'スマートフォンの認証アプリ（Google Authenticator / Microsoft Authenticator）に表示されている 6 桁の確認コードを入力してください。'
          : '保管しているリカバリーコードを 1 つ入力してください。使用したコードは再度使えません。'}
      </p>
      {status.data && (
        <p className="bs-mfa-account">
          <ShieldCheck size={15} aria-hidden /> {status.data.name}（{status.data.loginId}）
        </p>
      )}
      {error && (
        <div className="bs-login__alert" role="alert">
          <AlertCircle size={18} aria-hidden />
          <span>{error}</span>
        </div>
      )}
      <form onSubmit={onSubmit} noValidate className="bs-login__form">
        {mode === 'code' ? (
          <FormField label="確認コード">
            <MfaCodeInput value={code} onChange={setCode} autoFocus />
          </FormField>
        ) : (
          <FormField label="リカバリーコード" hint="ハイフンの有無・大文字小文字は問いません">
            <TextField
              autoFocus
              autoComplete="off"
              prefix={<KeyRound size={16} />}
              placeholder="abcde-23456"
              value={recovery}
              onChange={(e) => setRecovery(e.target.value)}
            />
          </FormField>
        )}
        <Button type="submit" variant="primary" size="lg" block loading={submitting} icon={<ShieldCheck size={18} />}>
          確認してログイン
        </Button>
      </form>
      <div className="bs-login__links">
        <button
          type="button"
          className="bs-login__back"
          onClick={() => {
            setMode(mode === 'code' ? 'recovery' : 'code');
            setError(null);
          }}
        >
          {mode === 'code' ? '認証アプリを使えない場合（リカバリーコードを使用）' : '認証アプリの確認コードを使用する'}
        </button>
        <BackToLogin />
      </div>
      <ul className="bs-login__notes">
        <li>確認コードは 30 秒ごとに変わります。入力が間に合わない場合は次のコードを入力してください。</li>
        <li>スマートフォンを紛失した場合は、振興基金の担当者に 2 段階認証のリセットを依頼してください。</li>
      </ul>
    </LoginShell>
  );
}

/** 2段階認証：認証アプリの登録（必須ユーザーの初回ログイン時） */
export function MfaSetupPage() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const { status, redirect, to } = usePendingGuard('setup');
  const [done, setDone] = useState<{ codes: string[]; user: AuthUser } | null>(null);

  useEffect(() => {
    document.title = `2段階認証の設定｜${APP_NAME}`;
  }, []);

  // 有効化後（リカバリーコード表示中）はガードしない
  if (to && !done) return <Navigate to={to} replace />;
  if (status.isPending && !done) return <Loading fullscreen label="ログイン状態を確認しています" />;

  return (
    <LoginShell wide>
      <h1 className="bs-login__title bs-login__title--sm">2段階認証の設定</h1>
      {done ? (
        <RecoveryCodes
          codes={done.codes}
          loginId={done.user.loginId}
          doneLabel="利用を開始する"
          onDone={() => {
            completeLogin(done.user);
            navigate(redirect, { replace: true });
          }}
        />
      ) : (
        <>
          <p className="bs-login__sub">
            このアカウントは 2 段階認証が必須に設定されています。スマートフォンの認証アプリを登録してから利用を開始してください。
          </p>
          <MfaEnrollment
            onEnabled={(r) => {
              if (r.user) setDone({ codes: r.recoveryCodes, user: r.user });
            }}
          />
          <div className="bs-login__links">
            <BackToLogin />
          </div>
        </>
      )}
    </LoginShell>
  );
}
